import { useEffect, useState, type FormEvent } from 'react';
import { Dialog } from './Dialog';
import { Button } from './Button';
import { Input } from './Input';
import { t } from '@/i18n';

export interface PromptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  label: string;
  initial?: string;
  placeholder?: string;
  submitLabel?: string;
  onSubmit: (value: string) => Promise<unknown> | unknown;
  description?: string;
}

/** Диалог с одним полем ввода (переименовать, создать проект/воркспейс). */
export function PromptDialog({ open, onOpenChange, title, label, initial = '', placeholder, submitLabel = t('common.save'), onSubmit, description }: PromptDialogProps) {
  const [value, setValue] = useState(initial);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (open) setValue(initial);
  }, [open, initial]);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!value.trim()) return;
    setLoading(true);
    try {
      await onSubmit(value.trim());
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={title} description={description} size="sm">
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Input label={label} value={value} placeholder={placeholder} onChange={(e) => setValue(e.target.value)} autoFocus maxLength={200} />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" loading={loading} disabled={!value.trim()}>
            {submitLabel}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** Подтверждение опасного действия. */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  text,
  confirmLabel,
  onConfirm,
  danger = true,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  text: string;
  confirmLabel: string;
  onConfirm: () => Promise<unknown> | unknown;
  danger?: boolean;
}) {
  const [loading, setLoading] = useState(false);
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={title} size="sm">
      <p className="text-sm text-fg-muted">{text}</p>
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="ghost" onClick={() => onOpenChange(false)}>
          {t('common.cancel')}
        </Button>
        <Button
          variant={danger ? 'danger' : 'primary'}
          loading={loading}
          onClick={async () => {
            setLoading(true);
            try {
              await onConfirm();
              onOpenChange(false);
            } finally {
              setLoading(false);
            }
          }}
        >
          {confirmLabel}
        </Button>
      </div>
    </Dialog>
  );
}
